import React, { useEffect, useState, useCallback } from 'react';

interface VersionInfo {
  version: string;
  buildDate: string;
  buildNumber: string;
}

interface VersionCheckerProps {
  checkInterval?: number; // in milliseconds, default 5 minutes
  autoReloadDelay?: number; // in seconds, default 10 seconds
}

const VersionChecker: React.FC<VersionCheckerProps> = ({ 
  checkInterval = 5 * 60 * 1000, // 5 minutes
  autoReloadDelay = 10 // 10 seconds
}) => {
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [countdown, setCountdown] = useState(autoReloadDelay);

  // Fetch current version
  const fetchVersion = useCallback(async (): Promise<string | null> => {
    try {
      // Add timestamp to prevent caching
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (response.ok) {
        const data: VersionInfo = await response.json();
        return data.version;
      }
    } catch (error) {
      console.log('Version check skipped (version.json not found)');
    }
    return null;
  }, []);

  // Initialize version
  useEffect(() => {
    const initVersion = async () => {
      const version = await fetchVersion();
      if (version) {
        setCurrentVersion(version);
        console.log('📦 Current app version:', version);
      }
    };
    initVersion();
  }, [fetchVersion]);

  // Check for updates periodically
  useEffect(() => {
    if (!currentVersion) return;

    const checkForUpdates = async () => {
      const latestVersion = await fetchVersion();
      
      if (latestVersion && latestVersion !== currentVersion) {
        console.log('🔄 New version detected!', { current: currentVersion, latest: latestVersion });
        setShowUpdateModal(true);
        setCountdown(autoReloadDelay);
      }
    };

    // Check immediately
    checkForUpdates();

    // Then check periodically
    const interval = setInterval(checkForUpdates, checkInterval);

    return () => clearInterval(interval);
  }, [currentVersion, checkInterval, autoReloadDelay, fetchVersion]);

  // Countdown timer for auto-reload
  useEffect(() => {
    if (!showUpdateModal) return;

    if (countdown <= 0) {
      handleReload();
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [showUpdateModal, countdown]);

  const handleReload = () => {
    window.location.reload();
  };

  const handleDismiss = () => {
    setShowUpdateModal(false);
    // Reset countdown for next time
    setCountdown(autoReloadDelay);
  };

  if (!showUpdateModal) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in">
      <div className="relative max-w-md w-full">
        {/* Glassmorphism Card - Same design as login/loading */}
        <div className="relative backdrop-blur-3xl bg-white/90 border border-white/40 rounded-3xl shadow-2xl shadow-indigo-200/50 p-8 transform transition-all duration-300 before:absolute before:inset-0 before:rounded-3xl before:bg-gradient-to-br before:from-white/40 before:to-white/10 before:pointer-events-none animate-scale-in">
          {/* Decorative gradient blob */}
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-r from-purple-400 to-blue-500 rounded-full blur-3xl opacity-30 animate-pulse"></div>
          
          <div className="relative z-10">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl blur-lg opacity-50 animate-pulse"></div>
                <div className="relative bg-gradient-to-r from-indigo-500 to-purple-500 p-4 rounded-2xl shadow-lg">
                  <svg className="w-10 h-10 text-white animate-bounce-slow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-3 tracking-tight">
              New Update Available! 🎉
            </h2>

            {/* Description */}
            <p className="text-gray-700 text-center mb-6 leading-relaxed">
              A new version of the application is available. The page will automatically reload to get the latest features and improvements.
            </p>

            {/* Countdown indicator */}
            <div className="bg-indigo-50/80 backdrop-blur-sm border border-indigo-200 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-center gap-3">
                <div className="relative">
                  <svg className="w-12 h-12 transform -rotate-90">
                    <circle
                      cx="24"
                      cy="24"
                      r="20"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                      className="text-indigo-200"
                    />
                    <circle
                      cx="24"
                      cy="24"
                      r="20"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 20}`}
                      strokeDashoffset={`${2 * Math.PI * 20 * (1 - countdown / autoReloadDelay)}`}
                      className="text-indigo-600 transition-all duration-1000"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xl font-bold text-indigo-600">{countdown}</span>
                  </div>
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-900">Auto-reloading in {countdown} seconds</p>
                  <p className="text-xs text-gray-600">Your work has been saved</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleDismiss}
                className="flex-1 py-3 px-4 bg-white/60 backdrop-blur-xl border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-white/80 hover:border-gray-400 transition-all duration-200 shadow-sm"
              >
                Remind Later
              </button>
              <button
                onClick={handleReload}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-indigo-500/50 transition-all duration-200 shadow-lg group relative overflow-hidden"
              >
                {/* Button shine effect */}
                <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                <span className="relative flex items-center justify-center gap-2">
                  Reload Now
                  <svg className="w-5 h-5 group-hover:rotate-180 transition-transform duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </span>
              </button>
            </div>

            {/* Additional info */}
            <p className="text-xs text-center text-gray-500 mt-4 flex items-center justify-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Don't worry, your progress is automatically saved
            </p>
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default VersionChecker;

